import type { Breakpoint } from './theme/breakpoints';
import { BREAKPOINT_ORDER } from './theme/breakpoints';

// primitive types
export type Primitive = string | number | boolean;

// helper type: token must be a Primitive and non-null
type TokenOf<T> = Extract<NonNullable<T>, Primitive>;

// Responsive prop that can be a single value or an object keyed by breakpoints
// breakpoint object e.g { base: 'value', md: 'value' }
export type ResponsiveValue<T extends Primitive> =
  | T
  | Partial<Record<Breakpoint, T>>;

// Upgrade only the keys in K to be responsive, allowing for undefined values
export type WithResponsiveProps<T, K extends keyof T> = Omit<T, K> & {
  // Each key in K becomes a responsive prop
  // e.g. { size?: ResponsiveValue<string>, textAlign?: ResponsiveValue<string> }
  [P in K]?: ResponsiveValue<TokenOf<T[P]>>;
};

// Map of CSS module classes keyed by responsive breakpoint
// e.g. { 'base:prop-value': 'hash', 'md:prop-value': 'hash' }
export type StylesMap = Record<string, string>;

/**
 * getBaseValue is a utility function that extracts the base value
 * from a responsive value object or returns a fallback value.
 * It is used to resolve the base value for responsive props.
 * @param value
 * @param fallback
 * @returns
 */
export function getBaseValue<T extends Primitive>(
  value: ResponsiveValue<T> | undefined,
  fallback: T,
): T {
  if (value == null) return fallback;
  return typeof value === 'object' ? (value.base ?? fallback) : value;
}

/**
 * getBaseValueOrUndefined
 * --------------------------------------------------------------------------
 * Extracts the **base (non-responsive)** value from a responsive prop,
 * or returns `undefined` if none exists.
 *
 * ✅ Returns the `base` key if present in a responsive object.
 * ✅ Returns the primitive value if the prop is a single token.
 * ✅ Returns `undefined` when:
 *    - the prop itself is undefined/null, or
 *    - the responsive object has no `base` key.
 *
 * Used when you want CVA (or component defaults) to decide the base value.
 *
 * Examples:
 *  getBaseValueOrUndefined('center') → 'center'
 *  getBaseValueOrUndefined({ base: 'center', md: 'end' }) → 'center'
 *  getBaseValueOrUndefined({ md: 'center' }) → undefined
 *  getBaseValueOrUndefined(undefined) → undefined
 */
export function getBaseValueOrUndefined<T extends string | number | boolean>(
  value: ResponsiveValue<T> | null | undefined,
): T | undefined {
  if (value == null) return undefined; // handles null & undefined
  return typeof value === 'object' ? value.base : value;
}

/**
 * toKebab is a utility function that converts a camelCase string
 * to kebab-case. It is used to generate class names from
 * camelCase property names.
 * @param str
 * @returns
 */
const toKebab = (str: string) =>
  str.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase()).replaceAll('.', '_');

/**
 * generateResponsiveClasses is a utility function that generates
 * responsive class names based on a styles map and a responsive value.
 * It returns a function that can be used to get class names for
 * different breakpoints.
 * @param stylesMap
 * @returns
 */
export function generateResponsiveClasses(stylesMap: StylesMap) {
  return function getResponsiveClasses<T extends Primitive>(
    value: ResponsiveValue<T> | null | undefined, // ⬅ accept null
    propClassPrefix: string,
    booleanClasses?: { trueClass: string; falseClass: string },
  ): string[] {
    if (value == null || typeof value !== 'object') return []; // ⬅ guard null/undefined/primitives

    return (Object.entries(value) as [Breakpoint, T][])
      .filter(([bp]) => bp !== 'base')
      .sort(
        ([a], [b]) => BREAKPOINT_ORDER.indexOf(a) - BREAKPOINT_ORDER.indexOf(b),
      )
      .map(([bp, raw]) => {
        if (typeof raw === 'boolean') {
          const on = booleanClasses?.trueClass ?? `${propClassPrefix}--true`;
          const off = booleanClasses?.falseClass ?? `${propClassPrefix}--false`;
          return stylesMap[`${bp}:${raw ? on : off}`];
        }
        const suffix = toKebab(String(raw));
        return stylesMap[`${bp}:${propClassPrefix}--${suffix}`];
      })
      .filter(Boolean) as string[];
  };
}

/**
 * buildResponsiveKey is a utility function that builds a responsive
 * key for a given breakpoint, property class prefix, and value.
 * It is used to create class names for responsive props.
 * @param bp
 * @param propClassPrefix
 * @param value
 * @returns
 */
export function buildResponsiveKey<T extends Primitive>(
  bp: Exclude<Breakpoint, 'base'>,
  propClassPrefix: string,
  value: T,
): string {
  return typeof value === 'boolean'
    ? `${bp}:${propClassPrefix}--${value ? 'true' : 'false'}`
    : `${bp}:${propClassPrefix}--${toKebab(String(value))}`;
}
